import {
  Accessor,
  Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  onMount,
  Setter,
} from "solid-js";
// import "./modal.css";
import { classNames } from "../utils/etc";
import { routerLog } from "~/utils/logging";

import {
  closeIntercom,
  isIntercomVisible,
  setIsIntercomVisible,
} from "~/utils/third_party/intercom";
import { CloseIcon } from "~/assets/svg_icons/svg_icons";

function attachPopStateListener() {
  routerLog("Attached pop state listener");
  window.onpopstate = popStateListener;
}

function detachPopStateListener() {
  window.onpopstate = null;
  routerLog(`Popstate listener detached`);
}

async function popStateListener() {
  routerLog("popStateListener called");

  if (isIntercomVisible()) {
    setIsIntercomVisible(false);
    closeIntercom();
    return;
  }

  if (modals().length > 0) {
    popWithAnimation();
  }
}

export type ModalSize = "small" | "medium" | "large";

export interface CurrentPage {
  builder: Component<{ page?: CurrentPage }>;
  isModal: boolean;
  isOpen: Accessor<boolean>;
  setIsOpen: Setter<boolean>;
  isContentVisible: Accessor<boolean>;
  setIsContentVisible: Setter<boolean>;
  preventClose?: boolean;
  size?: ModalSize;
  expandToFullHeight?: boolean;
}

const [modals, setModals] = createSignal<CurrentPage[]>([]);

export function openModalV2(
  builder: Component<{ page?: CurrentPage }>,
  options: {
    preventClose?: boolean;
    size?: ModalSize;
    expandToFullHeight?: boolean;
  } = {}
) {
  const [isOpen, setIsOpen] = createSignal<boolean>(true);

  const [isContentVisible, setIsContentVisible] = createSignal<boolean>(false);

  history.pushState({}, "", "");

  setModals([
    ...modals(),
    {
      builder,
      isContentVisible,
      setIsContentVisible,
      isModal: true,
      isOpen,
      setIsOpen,
      expandToFullHeight: options.expandToFullHeight,
      preventClose: options.preventClose,
      size: options.size,
    },
  ]);
}

function popPage() {
  routerLog(`doing popPage`);
  if (modals().length == 0) return;
  setModals(modals().slice(0, -1));
}

let onPopCallBack = () => {};

onMount(() => {
  window.addEventListener("popstate", (event) => {
    if (event.state) {
      setTimeout(() => {
        // todo: this should be cleaned later
        try {
          onPopCallBack();
          onPopCallBack = () => {};
        } catch (error) {
          console.error(error);
        }
      }, 260);
    }
  });
});

export function goBack(callback?: () => void) {
  onPopCallBack = callback || (() => {});

  window.history.back();
  routerLog(`window.history.back complete`);
}

function popWithAnimation() {
  routerLog(`doing popWithAnimation`);
  const modal = modals()[modals().length - 1];

  modal.setIsContentVisible(false);
  setTimeout(() => {
    popPage();
  }, 250);
}

/**
 * Closes all modals and navigates back to the previous page.
 * This is only for closing the modals programmatically.
 * use goBack() to close a modal by user action.
 * @returns {Promise<void>}
 */
export async function closeAllModals(): Promise<void> {
  const n = modals().length;
  routerLog(`going back ${n} times`);
  detachPopStateListener();
  setModals([]);
  for (let i = 0; i < n; i++) {
    // this is for removing the modals from the history stack
    goBack();
  }
  await new Promise((resolve) => setTimeout(resolve, 350));
  attachPopStateListener();
}

export function ModalHost() {
  onMount(() => {
    attachPopStateListener();
    return () => {
      detachPopStateListener();
    };
  });

  // set enterAnimation to false for last page after 250 ms

  const areModalsOpen = createMemo(() => {
    return modals().length > 0;
  });

  createEffect(() => {
    if (areModalsOpen()) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflowY = "auto";
      document.body.style.paddingRight = "";
    }
  });

  return (
    <For each={modals()}>
      {(modal, index) => {
        return <ModalLayout modal={modal} />;
      }}
    </For>
  );
}

function ModalLayout(props: { modal: CurrentPage }) {
  const closeModal = (event: MouseEvent) => {
    event.stopPropagation();
    goBack();
  };

  onMount(() => {
    setTimeout(() => {
      props.modal.setIsContentVisible(true);
    }, 100);
  });

  return (
    <div
      class=" fixed inset-0 z-50 h-full w-full transition-colors duration-300 ease-in-out"
      style={{
        "background-color": props.modal.isContentVisible()
          ? "rgba(0, 0, 0, 0.4)"
          : "rgba(0, 0, 0, 0)",
      }}
      onClick={props.modal.preventClose ? undefined : closeModal}
    >
      <div
        class="no-scrollbar absolute bottom-0 mx-auto h-min max-h-[70vh] w-full overflow-x-hidden overflow-y-scroll  rounded-t-2xl border border-stroke-1 bg-background-normal-primary transition-all duration-300  sm:inset-0 sm:my-auto sm:mb-auto   sm:rounded-b-2xl"
        classList={{
          "opacity-0": !props.modal.isContentVisible(),
          "opacity-100": props.modal.isContentVisible(),
          "translate-y-0": props.modal.isContentVisible(),
          "translate-y-[100px]": !props.modal.isContentVisible(),
          // "transition-opacity duration-300": true,
          "sm:max-w-[320px]": props.modal.size === "small",
          "sm:max-w-[440px]":
            props.modal.size === "medium" || !props.modal.size,
          "sm:max-w-[560px]": props.modal.size === "large",
          "min-h-[70vh]": props.modal.expandToFullHeight,
          // "sm:max-w-[440px]": !props.modal.size,
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {props.modal.builder({ page: props.modal })}
      </div>
    </div>
  );
}

export function CloseButton(props: {
  class?: string;
  onClick?: (event: MouseEvent) => void;
  iconClass?: string;
}) {
  return (
    <div class={classNames(props.class, "closeButton")} onClick={props.onClick}>
      <CloseIcon class={props.iconClass} />
    </div>
  );
}
